/**
 * Hook for fetching contact health score + mounted health history.
 * Current score still comes from the scoring intelligence route while
 * historical trend comes from the mounted people route.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

export interface HealthScoreData {
  compositeScore: number;
  dimensionScores: Record<string, number>;
  computedAt: string;
}

interface HealthScoreApiResponse {
  score: number;
  status: string | null;
  computedAt: string;
}

interface HealthScoreHistoryEntry {
  compositeScore: number;
  dimensionScores: Record<string, number> | null;
  computedAt: string;
}

export type HealthTrendDirection = "accelerating" | "stable" | "decelerating" | null;

const MOMENTUM_DELTA_THRESHOLD = 5;

function deriveTrendDirection(history: HealthScoreData[]): HealthTrendDirection {
  if (history.length < 2) {
    return null;
  }

  const first = history[0]?.compositeScore;
  const last = history[history.length - 1]?.compositeScore;

  if (first == null || last == null) {
    return null;
  }

  const delta = last - first;
  if (delta >= MOMENTUM_DELTA_THRESHOLD) {
    return "accelerating";
  }
  if (delta <= -MOMENTUM_DELTA_THRESHOLD) {
    return "decelerating";
  }
  return "stable";
}

export function useCrmHealthScore(contactId: string | null) {
  const [current, setCurrent] = useState<HealthScoreData | null>(null);
  const [history, setHistory] = useState<HealthScoreData[]>([]);
  const [trendDirection, setTrendDirection] = useState<HealthTrendDirection>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHealthScore = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [scoreRes, historyRes] = await Promise.all([
        apiFetch<{ data: HealthScoreApiResponse | null }>(
          `/crm/intelligence/contacts/${contactId}/health-score`,
        ),
        apiFetch<{ data: HealthScoreHistoryEntry[] }>(
          `/crm/people/${contactId}/health-history?days=90`,
        ),
      ]);

      if (scoreRes.data) {
        setCurrent({
          compositeScore: scoreRes.data.score,
          dimensionScores: {},
          computedAt: scoreRes.data.computedAt,
        });
      } else {
        setCurrent(null);
      }

      const nextHistory = (historyRes.data ?? []).map((entry) => ({
        compositeScore: entry.compositeScore,
        dimensionScores: entry.dimensionScores ?? {},
        computedAt: entry.computedAt,
      }));

      setHistory(nextHistory);
      setTrendDirection(deriveTrendDirection(nextHistory));
    } catch {
      setError("Failed to load health score");
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  useEffect(() => { fetchHealthScore(); }, [fetchHealthScore]);

  return { current, history, trendDirection, isLoading, error, refetch: fetchHealthScore };
}
